import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { WeatherReport } from '../utils/api';
import { useReports } from '../hooks/useReports';
import Card, { CardHeader, CardContent } from './Card';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import './ReportList.css';

interface ReportListProps {
  className?: string;
}

interface DeleteConfirmationProps {
  report: WeatherReport;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({
  report,
  onConfirm,
  onCancel,
}) => (
  <div className="delete-confirmation-overlay">
    <div className="delete-confirmation-modal">
      <h3>Confirmar Eliminación</h3>
      <p>
        ¿Estás seguro de que deseas eliminar el informe "{report.title}"?
      </p>
      <p className="delete-warning">
        Esta acción no se puede deshacer. Se eliminarán todos los archivos multimedia asociados.
      </p>
      <div className="delete-actions">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Eliminar Informe
        </Button>
      </div>
    </div>
  </div>
);

const ReportList: React.FC<ReportListProps> = ({ className = '' }) => {
  const { reports, isLoading, error, refreshReports, deleteReport } = useReports();
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [reportToDelete, setReportToDelete] = useState<WeatherReport | null>(null);

  const handleDeleteClick = (report: WeatherReport) => {
    setReportToDelete(report);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;

    setDeletingReportId(reportToDelete.reportId);
    const success = await deleteReport(reportToDelete.reportId);
    
    if (success) {
      setReportToDelete(null);
    }
    setDeletingReportId(null);
  };

  const handleDeleteCancel = () => {
    setReportToDelete(null);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-AR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  if (isLoading) {
    return (
      <div className={`report-list-loading ${className}`}>
        <LoadingSpinner />
        <p>Cargando informes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`report-list-error ${className}`}>
        <Card>
          <CardContent>
            <div className="error-content">
              <h3>Error al cargar informes</h3>
              <p>{error}</p>
              <Button onClick={refreshReports} variant="primary">
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`report-list ${className}`}>
      <Card>
        <CardHeader>
          <div className="report-list-header">
            <h2>Gestión de Informes</h2>
            <Link to="/admin/reports">
              <Button variant="primary">
                Crear Nuevo Informe
              </Button>
            </Link>
          </div>
        </CardHeader>

        <CardContent>
          {reports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>No hay informes</h3>
              <p>Aún no se han creado informes meteorológicos.</p>
              <Link to="/admin/reports">
                <Button variant="primary">
                  Crear Primer Informe
                </Button>
              </Link>
            </div>
          ) : (
            <div className="reports-grid">
              {reports.map((report) => (
                <div key={report.reportId} className="report-item">
                  <div className="report-content">
                    <div className="report-header">
                      <h3 className="report-title">{report.title}</h3>
                      <div className="report-meta">
                        <span className="report-author">Por {report.authorName}</span>
                        <span className="report-date">
                          {formatDate(report.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className="report-preview">
                      <p>{truncateContent(report.content)}</p>
                    </div>

                    {report.mediaFiles && report.mediaFiles.length > 0 && (
                      <div className="report-media-info">
                        <span className="media-count">
                          📎 {report.mediaFiles.length} archivo{report.mediaFiles.length !== 1 ? 's' : ''} multimedia
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="report-actions">
                    <Link to={`/admin/reports/${report.reportId}`}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={deletingReportId === report.reportId}
                      >
                        ✏️ Editar
                      </Button>
                    </Link>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(report)}
                      loading={deletingReportId === report.reportId}
                      disabled={deletingReportId === report.reportId}
                    >
                      🗑️ Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation modal */}
      {reportToDelete && (
        <DeleteConfirmation
          report={reportToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}
    </div>
  );
};

export default ReportList;